import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../formField';
import { FormValidator } from '../../formField/types/form-validator';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { IFormGroupChild } from '../../formGroup/types/form-group-children';
import { FormListItem } from '../types/FormListItem';
import { elementClassListAdd } from '../../../../utils/htmlElement.utils';

export class GzFormListWebComponent extends LitElement implements IFormGroupChild {
  @property({ type: Boolean, reflect: true, attribute: true })
  public disabled: boolean;

  @property({ type: Boolean, reflect: true, attribute: true })
  public useMinWidth = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  public useLargeInput = false;

  @property({ type: Boolean, reflect: true, attribute: true })
  public readonly: boolean;

  @property({ type: Array, reflect: true, attribute: true })
  private formListItems: FormListItem[] = [];

  protected validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public valid$ = this.validSubject.pipe(distinctUntilChanged());

  private validators: FormValidator[] = [];
  private formListWebComponents: GzFormFieldWebComponent[] = [];

  public get valid() {
    return this.validSubject.value;
  }

  public get value(): FormListItem[] {
    const value: FormListItem[] = [];

    for (const formField of this.formListWebComponents) {
      if (formField.value) {
        value.push({
          id: formField.id,
          name: formField.label,
          value: formField.value,
        });
      }
    }

    return value;
  }

  public set value(value: FormListItem[]) {
    this.formListItems = value;
    this.formListWebComponents = [];

    for (const formListItem of this.formListItems) {
      this.addFormListWebComponent(formListItem);
    }
  }

  public static get styles() {
    return styles;
  }

  public set Validators(validators: FormValidator[]) {
    this.validators = validators;
    this.updateValidatorsForEachFormField();
  }

  public get hasUnsavedChanges(): boolean {
    for (const formField of this.formListWebComponents) {
      if (formField.hasUnsavedChanges) {
        return true;
      }
    }

    return false;
  }

  public refreshInitialFormValue(): void {
    for (const formField of this.formListWebComponents) {
      formField.refreshInitialFormValue();
    }
  }

  public addFormListItem(formListItem: FormListItem) {
    this.formListItems.push(formListItem);
    this.addFormListWebComponent(formListItem);
    this.requestUpdate();
  }

  private addFormListWebComponent(formListItem: FormListItem) {
    const formField = new GzFormFieldWebComponent();

    if (this.useMinWidth) {
      elementClassListAdd(formField, 'label-mw-250');
    }

    formField.innerHTML = `
      <gz-input gz-form-input
        placeholder='${formListItem.placeholder ? formListItem.placeholder : ''}'
        inputWidth='${this.useLargeInput ? 400 : 240}'
      ></gz-input>
      <gz-input-error></gz-input-error>
    `;
    if (formListItem.value) {
      formField.initialValue = formListItem.value;
      formField.value = formListItem.value;
    }
    formField.id = formListItem.id;
    formField.label = formListItem.name;
    formField.required = formListItem.required ?? false;
    formField.disabled = formListItem.disabled ?? false;
    formField.Validators = this.validators;

    formField.valid$.subscribe({
      next: () => {
        this.triggerValidation();
      },
    });

    this.formListWebComponents.push(formField);
  }

  private updateValidatorsForEachFormField() {
    for (const formField of this.formListWebComponents) {
      formField.Validators = this.validators;
    }
  }

  public validate(): void {
    if (this.disabled || this.readonly) {
      return;
    }

    for (const formField of this.formListWebComponents) {
      formField.validate();
    }
  }

  protected triggerValidation(force = false) {
    let isValid = true;

    for (const formField of this.formListWebComponents) {
      isValid &&= formField.valid;
    }

    this.validSubject.next(isValid);
  }

  public render() {
    return html` <div class="form-list-wrapper">${this.formListWebComponents.map(formField => formField)}</div>`;
  }

  public discardChanges(): void {
    for (const formField of this.formListWebComponents) {
      formField.discardChanges();
    }
  }
}
